/** Map Kevine GET /api/funnel JSON → FunnelPage view model. */

import { LIVE_DATA_SOURCE_LABEL } from './journeyApiFilters';

function formatCount(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function formatRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return null;
  return `${Number(rate).toFixed(1)}%`;
}

function stageByKey(stages, key) {
  return stages.find((stage) => stage.key === key) || null;
}

function conversionByPair(conversions, from, to) {
  return conversions.find((row) => row.from === from && row.to === to) || null;
}

/**
 * Build stepped-bar stages from aggregate API counts.
 * Pending tickets stay on the first stage only (Jacob rule).
 */
function buildChartStages(api) {
  const stages = api.stages || [];
  const conversions = api.conversions || [];
  const ticket = stageByKey(stages, 'ticket_purchased');
  const scan = stageByKey(stages, 'scanned_in');
  const pos = stageByKey(stages, 'pos_spent');
  const ticketToScan = conversionByPair(conversions, 'ticket_purchased', 'scanned_in');
  const scanToPos = conversionByPair(conversions, 'scanned_in', 'pos_spent');

  const ticketsPast = Number(ticket?.count || 0);
  const ticketsPending = Number(api.pendingTickets || ticket?.pending || 0);
  const scans = Number(scan?.count || 0);
  const posCount = Number(pos?.count || 0);
  const noShow = Math.max(0, ticketsPast - scans);
  const noSpend = Math.max(0, scans - posCount);

  return [
    {
      id: 'ticket_purchased',
      index: '01',
      label: ticket?.label || 'Ticket purchased',
      total: ticketsPast + ticketsPending,
      converted: ticketsPast,
      dropped: 0,
      pending: ticketsPending,
      conversionLabel: null,
      droppedLabel: null,
      pendingLabel: ticketsPending > 0 ? `${formatCount(ticketsPending)} pending` : null,
    },
    {
      id: 'scanned_in',
      index: '02',
      label: scan?.label || 'Scanned in',
      total: ticketsPast,
      converted: scans,
      dropped: noShow,
      pending: 0,
      conversionLabel: formatRate(ticketToScan?.rate),
      droppedLabel: noShow > 0 ? `−${formatCount(noShow)} no-show` : null,
      pendingLabel: null,
    },
    {
      id: 'pos_spent',
      index: '03',
      label: pos?.label || 'Spent at POS',
      subtitle: 'optional',
      total: scans,
      converted: posCount,
      dropped: noSpend,
      pending: 0,
      conversionLabel: formatRate(scanToPos?.rate),
      droppedLabel: noSpend > 0 ? `−${formatCount(noSpend)} no spend` : null,
      pendingLabel: null,
    },
  ];
}

function buildKpis(api, chartStages) {
  const ticketToScan = conversionByPair(api.conversions || [], 'ticket_purchased', 'scanned_in');
  const scanToPos = conversionByPair(api.conversions || [], 'scanned_in', 'pos_spent');
  const attach = api.attachRates || {};
  const scanStage = chartStages[1];
  const posStage = chartStages[2];

  const biggestDrop =
    (scanStage?.dropped || 0) >= (posStage?.dropped || 0)
      ? {
          value: 'Ticket → Scan',
          detail:
            scanStage?.dropped > 0
              ? `−${formatCount(scanStage.dropped)} · ${formatRate(ticketToScan?.rate) || 'n/a'} attended`
              : 'No drop-off yet (ACP may be empty)',
        }
      : {
          value: 'Scan → POS',
          detail:
            posStage?.dropped > 0
              ? `−${formatCount(posStage.dropped)} · ${formatRate(scanToPos?.rate) || 'n/a'} spent`
              : 'No drop-off yet (POS may be empty)',
        };

  return [
    {
      id: 'conversion',
      label: 'Attendance conversion',
      value: formatRate(ticketToScan?.rate) || '—',
      detail: `${formatCount(scanStage?.converted)} of ${formatCount(scanStage?.total)} eligible`,
    },
    {
      id: 'dropoff',
      label: 'Biggest drop-off',
      value: biggestDrop.value,
      detail: biggestDrop.detail,
    },
    {
      id: 'pending',
      label: 'Pending tickets',
      value: formatCount(api.pendingTickets || 0),
      detail: 'Excluded from %',
    },
    {
      id: 'addon_attach',
      label: 'Add-on attach',
      value: formatRate(attach.addonAttachRate) || '—',
      detail: `${formatCount(attach.addonWithAdmission)} of ${formatCount(attach.admissionInvoices)} admissions`,
    },
  ];
}

function buildSteps(api, chartStages) {
  const rows = chartStages.map((stage, index) => ({
    id: stage.id,
    step: `${stage.index} ${stage.label}`,
    entered: formatCount(stage.total),
    converted: stage.conversionLabel
      ? `${formatCount(stage.converted)} (${stage.conversionLabel})`
      : `${formatCount(stage.converted)} eligible`,
    dropOff: stage.dropped > 0 ? formatCount(stage.dropped) : '—',
    medianTime: index === 0 ? '—' : 'Aggregate window',
  }));

  const pending = Number(api.pendingTickets || 0);
  if (pending > 0) {
    rows.push({
      id: 'pending',
      step: 'Pending — not yet played',
      entered: formatCount(pending),
      converted: 'held',
      dropOff: 'not counted',
      medianTime: api.pendingNote ? 'See pending note' : '—',
      isPending: true,
    });
  }

  return rows;
}

function buildConfidence(api) {
  const gaps = api.dataGaps || [];
  const hasGap = gaps.length > 0;
  const pending = Number(api.pendingTickets || 0);
  const title = hasGap
    ? pending > 0
      ? `Limited confidence — ${formatCount(pending)} pending held out. ${gaps[0]}`
      : `Limited confidence — ${gaps[0]}`
    : pending > 0
      ? `High confidence for CMP tickets — ${formatCount(pending)} pending held out.`
      : 'High confidence for CMP ticket counts.';

  return {
    tone: hasGap ? 'warning' : 'success',
    title: title.length > 160 ? `${title.slice(0, 157)}…` : title,
  };
}

function buildInsight(api, chartStages) {
  const scan = chartStages[1];
  const pos = chartStages[2];
  if ((pos?.dropped || 0) > (scan?.dropped || 0) && (pos?.total || 0) > 0) {
    return {
      eyebrow: 'Biggest weak point',
      title: `${formatCount(pos.dropped)} attendees recorded no POS spend in this window.`,
      action: 'Confirm POS ingest, then trial extra mobile POS on midweek fixtures.',
    };
  }
  if ((scan?.dropped || 0) > 0) {
    return {
      eyebrow: 'Biggest weak point',
      title: `${formatCount(scan.dropped)} eligible tickets never scanned in.`,
      action: 'Send a morning-of reminder on the next midweek home game.',
    };
  }
  if ((scan?.converted || 0) === 0) {
    return {
      eyebrow: 'Data gap',
      title: 'ACP scan data is not flowing yet — attendance conversion stays at zero.',
      action: api.dataGaps?.[0] || 'Ask platform team when ACP events will hit vi_events.',
    };
  }
  return {
    eyebrow: 'Funnel',
    title: api.identityNote || 'Aggregate stage counts for the selected window.',
    action: 'Keep pending fixtures excluded from conversion.',
  };
}

export function mapFunnelApiToView(api) {
  const chartStages = buildChartStages(api);
  return {
    mode: 'attendance',
    dataSourceLabel: LIVE_DATA_SOURCE_LABEL,
    confidence: buildConfidence(api),
    kpis: buildKpis(api, chartStages),
    chart: {
      title: 'Attendance conversion',
      stages: chartStages,
    },
    pending: {
      total: Number(api.pendingTickets || 0),
      fixtures: [],
      note: api.pendingNote || null,
    },
    insight: buildInsight(api, chartStages),
    stageInsights: {
      ticket_purchased: {
        eyebrow: 'Baseline',
        title: `${formatCount(chartStages[0].converted)} eligible tickets · ${formatCount(chartStages[0].pending)} pending.`,
        action: 'Pending is held out — focus on played fixtures only.',
      },
      scanned_in: {
        eyebrow: 'Attendance',
        title: `${formatCount(chartStages[1].converted)} scanned in of ${formatCount(chartStages[1].total)} eligible.`,
        action: chartStages[1].dropped
          ? 'Reduce no-shows with morning-of reminders.'
          : 'ACP may still be empty — confirm ingest.',
      },
      pos_spent: {
        eyebrow: 'On-site spend',
        title: `${formatCount(chartStages[2].converted)} POS transactions vs ${formatCount(chartStages[2].total)} scans.`,
        action: 'POS identity may still block person-level paths.',
      },
    },
    steps: buildSteps(api, chartStages),
    raw: api,
  };
}

/**
 * Membership / sales-channel endpoints already return FunnelPage-shaped payloads.
 * Normalize null KPI/stage values so the chart never crashes on missing channel data.
 */
function normalizeViewShapedFunnel(api, mode) {
  const stages = (api.chart?.stages || []).map((stage) => {
    const converted = stage.converted == null ? 0 : Number(stage.converted);
    const dropped = stage.dropped == null ? 0 : Number(stage.dropped);
    const pending = stage.pending == null ? 0 : Number(stage.pending);
    const unknownSplit = stage.converted == null && stage.dropped == null;
    return {
      ...stage,
      total: Number(stage.total || 0),
      converted,
      dropped,
      pending,
      conversionLabel: stage.conversionLabel || (unknownSplit ? '—' : null),
      droppedLabel: stage.droppedLabel || null,
      pendingLabel: stage.pendingLabel || null,
      // Keep a flag so the bar can show "n/a" instead of "0" for unknown splits.
      unavailable: unknownSplit,
    };
  });

  const steps =
    Array.isArray(api.steps) && api.steps.length > 0
      ? api.steps
      : stages.map((stage) => ({
          id: stage.id,
          step: `${stage.index} ${stage.label}`,
          entered: formatCount(stage.total),
          converted: stage.unavailable
            ? 'n/a — no channel field'
            : stage.conversionLabel
              ? `${formatCount(stage.converted)} (${stage.conversionLabel})`
              : formatCount(stage.converted),
          dropOff: stage.unavailable ? '—' : stage.dropped > 0 ? formatCount(stage.dropped) : '—',
          medianTime: '—',
        }));

  return {
    ...api,
    mode,
    dataSourceLabel: LIVE_DATA_SOURCE_LABEL,
    confidence: api.confidence || {
      tone: 'warning',
      title: 'Limited confidence — see data gaps.',
    },
    kpis: (api.kpis || []).map((kpi) => ({
      ...kpi,
      value: kpi.value == null || kpi.value === '' ? '—' : kpi.value,
    })),
    chart: {
      title: api.chart?.title || 'Funnel',
      stages,
    },
    insight: api.insight || {
      eyebrow: 'Funnel',
      title: 'Live funnel for the selected window.',
      action: 'Adjust filters and re-apply.',
    },
    stageInsights: api.stageInsights || {},
    steps,
    pending: api.pending || null,
    raw: api,
  };
}

export function mapMembershipFunnelApiToView(api) {
  return normalizeViewShapedFunnel(api, 'membership');
}

export function mapSalesChannelFunnelApiToView(api) {
  return normalizeViewShapedFunnel(api, 'sales_channel');
}
